import {
  passwordPolicyQuery,
  usePasswordPolicy,
} from "@common/api/password-policy"
import { useUpdatePasswordMeMutation } from "@common/api/users"
import { PasswordRequirements } from "@common/components/password-requirements"
import { Button } from "@common/components/ui/button"
import { Card, CardContent } from "@common/components/ui/card"
import { Input } from "@common/components/ui/input"
import { Label } from "@common/components/ui/label"
import { isLoggedIn } from "@common/hooks/use-auth"
import { queryClient } from "@common/lib/query-client"
import { confirmPasswordRules, handleError, passwordRules } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { Eye, EyeOff } from "lucide-react"
import { useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import useCustomToast from "../hooks/use-custom-toast"

interface FormData {
  current_password: string
  new_password: string
  confirm_password: string
}

export const Route = createFileRoute("/change-password")({
  component: ChangePassword,
  beforeLoad: async () => {
    if (!isLoggedIn()) {
      throw redirect({
        to: "/login",
      })
    }
    await queryClient.ensureQueryData(passwordPolicyQuery())
  },
})

function ChangePassword() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    mode: "onBlur",
  })

  const newPasswordValue = watch("new_password") ?? ""

  const showToast = useCustomToast()
  const navigate = useNavigate()
  const mutation = useUpdatePasswordMeMutation()
  const policy = usePasswordPolicy()

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    mutation.mutate(
      {
        updatePassword: {
          current_password: data.current_password,
          new_password: data.new_password,
        },
      },
      {
        onSuccess: (response) => {
          localStorage.setItem("access_token", response.access_token)
          showToast(
            "Password changed successfully",
            "Your password has been updated",
            "success",
          )
          navigate({ to: "/" })
        },
        onError: (err) => {
          handleError(err, showToast)
        },
      },
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="mb-4">
        <img
          src="/assets/images/fleetcraft-logo-indigo.svg"
          alt="Fleetcraft Logo"
          className="h-auto w-auto max-h-24 max-w-64"
        />
      </div>
      <Card className="overflow-hidden w-full max-w-md">
        <CardContent className="p-0">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-h1">Change Password</h1>
                <p className="text-balance text-muted-foreground">
                  Please update your password to continue
                </p>
              </div>

              {/* Current Password */}
              <div className="grid gap-2">
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    {...register("current_password", {
                      required: "Current password is required",
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.current_password && (
                  <p className="text-small-medium text-destructive">
                    {errors.current_password.message}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="grid gap-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? "text" : "password"}
                    {...register("new_password", passwordRules(policy))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.new_password && (
                  <p className="text-small-medium text-destructive">
                    {errors.new_password.message}
                  </p>
                )}
                <PasswordRequirements
                  policy={policy}
                  password={newPasswordValue}
                />
              </div>

              {/* Confirm Password */}
              <div className="grid gap-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    {...register(
                      "confirm_password",
                      confirmPasswordRules(getValues),
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="text-small-medium text-destructive">
                    {errors.confirm_password.message}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Changing password…" : "Change Password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
